import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  resolveEnrollmentPeriodAndScheduleOption,
  deriveWeekday,
} from "@/lib/enrollment";
import type { ClassSession } from "@/data/classes";

interface InterestFormProps {
  classId: string;
  className: string;
  sessions?: ClassSession[];
  /**
   * How many session options a parent may pick.
   *  - "choose_one_section" (default when >1 sessions): required single choice
   *  - "attend_all_days": no selector, all sessions implied
   *  - "optional_dates": multi-select (not yet used for interest form)
   */
  multiDayMode?: "choose_one_section" | "attend_all_days" | "optional_dates";
  /**
   * If provided, the form skips its own session selector and stores this
   * label / weekday / option on the interest row. Used by additional
   * "interest_only" schedule options (e.g., Monday LEGO Robotics).
   */
  presetOption?: {
    label: string;
    weekday?: string | null;
    option?: string | null;
    period?: string | null;
  };
  /**
   * Non-exclusive option choices for this class (e.g., D&D age group +
   * "Dad & Me"). When provided, the parent may check more than one and every
   * checked option is stored as its own structured `class_interest_options`
   * row against the SAME child record — never a duplicate child.
   */
  optionChoices?: {
    id: string;
    label: string;
    scheduleLine?: string | null;
    weekday?: string | null;
  }[];
  /** Helper copy shown above the multi-select option list. */
  optionChoicesHelp?: string;
  /** Optional headline override, e.g., "Join the Monday interest list". */
  title?: string;
  /** Optional intro copy override. */
  intro?: string;
  /** Fired after a successful insert so callers can refresh interest counts. */
  onSubmitted?: () => void;
}

const InterestForm = ({
  classId,
  className,
  sessions,
  multiDayMode = "choose_one_section",
  presetOption,
  optionChoices,
  optionChoicesHelp,
  title,
  intro,
  onSubmitted,
}: InterestFormProps) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    adult_name: "",
    email: "",
    child_name: "",
    child_age: "",
    topics: "",
  });

  const multiChoices = useMemo(() => optionChoices ?? [], [optionChoices]);
  const hasMultiChoices = multiChoices.length > 0;
  const [checkedLabels, setCheckedLabels] = useState<string[]>(
    presetOption ? [presetOption.label] : [],
  );
  const toggleChoice = (label: string) =>
    setCheckedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );

  // Only require a selection if there are ≥2 sessions AND the class is
  // "choose one section". If exactly one, preselect it silently. When a
  // presetOption is supplied, the selector is fully bypassed.
  const options = useMemo(() => (sessions ?? []).filter(Boolean), [sessions]);
  const requiresSelection =
    !presetOption &&
    !hasMultiChoices &&
    options.length > 1 &&
    multiDayMode === "choose_one_section";
  const [selectedLabel, setSelectedLabel] = useState<string>(
    presetOption ? presetOption.label : options.length === 1 ? options[0].label : "",
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const age = parseInt(form.child_age, 10);
    if (!form.adult_name.trim() || !form.email.trim() || !form.child_name.trim() || isNaN(age) || age < 1 || age > 18) {
      toast.error("Please fill out all fields with valid information.");
      return;
    }

    if (requiresSelection && !selectedLabel) {
      toast.error("Please choose which session you're interested in.");
      return;
    }

    if (hasMultiChoices && checkedLabels.length === 0) {
      toast.error("Please select at least one option you're interested in.");
      return;
    }

    const bannedEmails = ["followyourbliss@duck.com"];
    const bannedPatterns = [/\baltamirano\b/i, /\bmeeka\b/i, /\bmicaela\b/i, /\bmassi\b/i, /\bmazzi\b/i];
    const combined = `${form.adult_name} ${form.child_name}`;
    if (
      bannedEmails.includes(form.email.trim().toLowerCase()) ||
      bannedPatterns.some((re) => re.test(combined))
    ) {
      toast.error("Registration is not available for this account. Please contact silversagehomeschoolhubs@gmail.com.");
      return;
    }

    setLoading(true);
    const interestId = crypto.randomUUID();
    const adultName = form.adult_name.trim();
    const email = form.email.trim();
    const childName = form.child_name.trim();
    const topics = form.topics.trim() || null;

    // Resolve the chosen label into normalized period / option / weekday
    // using the same shared resolver the admin dashboards use. Legacy
    // rows submitted without a label stay NULL and appear under
    // "Day not selected" in the admin UI. Preset options (e.g., Monday
    // LEGO interest list) supply explicit values.
    //
    // With non-exclusive option choices, the parent row keeps a primary
    // label (the option they opened, or the first one they checked) while
    // EVERY checked option is written to `class_interest_options`.
    const primaryChoice = hasMultiChoices
      ? multiChoices.find(
          (c) => c.label === presetOption?.label && checkedLabels.includes(c.label),
        ) ?? multiChoices.find((c) => checkedLabels.includes(c.label)) ?? null
      : null;

    const resolved = primaryChoice
      ? { period: presetOption?.period ?? null, option: primaryChoice.label }
      : presetOption
        ? { period: presetOption.period ?? null, option: presetOption.option ?? presetOption.label }
        : selectedLabel
          ? resolveEnrollmentPeriodAndScheduleOption(selectedLabel)
          : null;
    const weekday = primaryChoice
      ? primaryChoice.weekday ?? deriveWeekday(primaryChoice.label)
      : presetOption
        ? presetOption.weekday ?? deriveWeekday(presetOption.label)
        : selectedLabel
          ? deriveWeekday(selectedLabel)
          : null;

    const { error } = await supabase.from("class_interest").insert({
      id: interestId,
      class_id: classId,
      adult_name: adultName,
      email,
      child_name: childName,
      child_age: age,
      topics,
      selected_session_label: primaryChoice?.label ?? selectedLabel ?? null,
      enrollment_period: resolved?.period ?? null,
      schedule_option: resolved?.option ?? weekday ?? null,
      weekday,
    });

    if (error) {
      setLoading(false);
      toast.error("Something went wrong. Please try again.");
      return;
    }

    // Structured option selections — one row per option this child is
    // interested in. Counts read from here, so a child in "Middle School"
    // and "Dad & Me" contributes exactly 1 to each bucket.
    const isUuid = (v: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    const selectionRows = hasMultiChoices
      ? multiChoices
          .filter((c) => checkedLabels.includes(c.label))
          .map((c) => ({
            interest_id: interestId,
            schedule_option_id: isUuid(c.id) ? c.id : null,
            class_id: classId,
            display_label: c.label,
          }))
      : resolved?.option ?? weekday
        ? [
            {
              interest_id: interestId,
              schedule_option_id: null,
              class_id: classId,
              display_label: (resolved?.option ?? weekday) as string,
            },
          ]
        : [];
    if (selectionRows.length > 0) {
      const { error: optErr } = await (supabase as any)
        .from("class_interest_options")
        .insert(selectionRows);
      if (optErr) console.error("Failed to store interest option selections:", optErr);
    }


    // Fire-and-forget welcome email. The server derives the recipient and the
    // email content from the committed interest row.
    try {
      await supabase.functions.invoke("send-interest-welcome", {
        body: { interestId },
      });
    } catch (emailErr) {
      console.error("Failed to send welcome email:", emailErr);
    }

    setLoading(false);
    setSubmitted(true);
    toast.success("Interest submitted! We'll be in touch.");
    onSubmitted?.();
  };


  if (submitted) {
    return (
      <div className="mt-6 rounded-lg border border-secondary/30 bg-secondary/10 p-5 text-center">
        <p className="font-heading text-lg font-bold text-foreground">Thank you!</p>
        <p className="text-sm text-muted-foreground mt-1">
          We've received your interest in <strong>{className}</strong> and will reach out when details are finalized.
        </p>
      </div>
    );
  }

  const isApprenticeship = classId === "teen-apprenticeship-program";

  return (
    <form onSubmit={handleSubmit} className="mt-6 rounded-lg border border-border bg-card p-5 space-y-4">
      <h4 className="font-heading text-lg font-bold text-foreground">
        {title ?? (isApprenticeship ? "Interested? Apply below!" : "Interested? Let us know!")}
      </h4>
      <p className="text-sm text-muted-foreground">
        {intro ?? (isApprenticeship
          ? "Fill out the form below and let us know which fields your teen is most excited to explore."
          : "Fill out the form below and we'll notify you when this class opens.")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="adult_name">Adult's Name</Label>
          <Input
            id="adult_name"
            required
            maxLength={100}
            value={form.adult_name}
            onChange={(e) => setForm({ ...form, adult_name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            required
            maxLength={255}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="child_name">Child's Name</Label>
          <Input
            id="child_name"
            required
            maxLength={100}
            value={form.child_name}
            onChange={(e) => setForm({ ...form, child_name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="child_age">Child's Age</Label>
          <Input
            id="child_age"
            type="number"
            required
            min={1}
            max={18}
            value={form.child_age}
            onChange={(e) => setForm({ ...form, child_age: e.target.value })}
          />
        </div>
      </div>

      {requiresSelection && (
        <div className="space-y-2">
          <Label>Which session are you interested in?</Label>
          <RadioGroup value={selectedLabel} onValueChange={setSelectedLabel}>
            {options.map((opt) => (
              <label
                key={opt.label}
                htmlFor={`session-${opt.label}`}
                className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/40"
              >
                <RadioGroupItem id={`session-${opt.label}`} value={opt.label} className="mt-1" />
                <div className="text-sm">
                  <div className="font-semibold text-foreground">{opt.label}</div>
                  {opt.dates && (
                    <div className="text-xs text-muted-foreground mt-0.5">{opt.dates}</div>
                  )}
                </div>
              </label>
            ))}
          </RadioGroup>
          <p className="text-xs text-muted-foreground">Choose one — you can email us later to switch.</p>
        </div>
      )}

      {hasMultiChoices && (
        <div className="space-y-2">
          <Label>Which options is your child interested in?</Label>
          <p className="text-xs text-muted-foreground">
            {optionChoicesHelp ??
              "Select all that apply — you may choose their age group and Dad & Me."}
          </p>
          <div className="space-y-2">
            {multiChoices.map((c) => (
              <label
                key={c.id}
                htmlFor={`choice-${c.id}`}
                className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/40"
              >
                <Checkbox
                  id={`choice-${c.id}`}
                  checked={checkedLabels.includes(c.label)}
                  onCheckedChange={() => toggleChoice(c.label)}
                  className="mt-1"
                />
                <div className="text-sm">
                  <div className="font-semibold text-foreground">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {c.scheduleLine || "Schedule TBD"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}


      {isApprenticeship && (
        <div className="space-y-1.5">
          <Label htmlFor="topics">
            Topics of Interest
          </Label>
          <Textarea
            id="topics"
            placeholder="e.g. Automotive, Physical Therapy, Journalism, Entrepreneurship, Photography..."
            value={form.topics}
            onChange={(e) => setForm({ ...form, topics: e.target.value })}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Let us know which fields your teen would most like to shadow someone in.
          </p>
        </div>
      )}

      <Button type="submit" disabled={loading} className="w-full font-body font-bold">
        {loading ? "Submitting…" : "Submit Interest"}
      </Button>
    </form>
  );
};

export default InterestForm;
